import { RadialBar, RadialBarChart, ResponsiveContainer } from 'recharts';
import { Icon } from '@shared/ui/icon/Icon';
import { cn } from '@shared/lib/cn';
import type { Receipt } from '@features/receipts';

/**
 * « AI Processing » — jauge circulaire de confiance + volumes par statut.
 */
export function AiProcessingCard({ receipts }: { receipts: Receipt[] }) {
  const total = receipts.length;
  const verified = receipts.filter((r) => (r.confidence ?? 0) >= 0.85).length;
  const needsReview = receipts.filter((r) => {
    const c = r.confidence ?? 0;
    return c >= 0.6 && c < 0.85;
  }).length;
  const failed = receipts.filter((r) => (r.confidence ?? 0) < 0.6).length;

  const avgConfidence =
    total === 0 ? 0 : receipts.reduce((sum, r) => sum + (r.confidence ?? 0), 0) / total;
  const confidencePercent = Math.round(avgConfidence * 100);

  const gaugeData = [{ value: confidencePercent, fill: '#059669' }];

  const stats = [
    {
      label: 'Succès',
      value: verified,
      icon: 'check-circle' as const,
      tone: 'text-emerald-600 bg-emerald-50',
    },
    {
      label: 'À revoir',
      value: needsReview,
      icon: 'sparkles' as const,
      tone: 'text-amber-600 bg-amber-50',
    },
    { label: 'Échoués', value: failed, icon: 'x' as const, tone: 'text-red-600 bg-red-50' },
  ];

  return (
    <div className="flex flex-col gap-5">
      {/* Jauge circulaire */}
      <div className="flex items-center gap-4 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
        <div className="relative size-20 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart
              innerRadius="78%"
              outerRadius="100%"
              data={gaugeData}
              startAngle={90}
              endAngle={-270}
            >
              <RadialBar dataKey="value" cornerRadius={8} background={{ fill: '#e2e8f0' }} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="text-sm font-bold tracking-tight text-slate-900">
              {total === 0 ? '—' : `${confidencePercent}%`}
            </span>
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-900">Confiance de l'extraction IA</p>
          <p className="text-xs text-slate-500">
            {total === 0 ? 'Aucune donnée' : `Basé sur ${total} ticket${total > 1 ? 's' : ''}`}
          </p>
        </div>
      </div>

      {/* Volume total + statuts colorés */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between rounded-xl border border-emerald-100 bg-emerald-50/50 px-3.5 py-2.5">
          <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <span className="flex size-7 items-center justify-center rounded-lg bg-emerald-600 text-white">
              <Icon name="sparkles" size={14} />
            </span>
            Tickets traités
          </span>
          <span className="text-lg font-semibold tracking-tight text-slate-900">{total}</span>
        </div>

        <ul className="grid grid-cols-3 gap-2.5">
          {stats.map((stat) => (
            <li
              key={stat.label}
              className="flex flex-col items-center gap-1 rounded-lg border border-slate-100 py-3"
            >
              <span
                className={cn('flex size-7 items-center justify-center rounded-full', stat.tone)}
              >
                <Icon name={stat.icon} size={14} />
              </span>
              <span className="text-base font-semibold tracking-tight text-slate-900">
                {stat.value}
              </span>
              <span className="text-[11px] text-slate-500">{stat.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
